import classNames from 'classnames';
import { i18n, TFunction } from 'i18next';
import memoizeOne from 'memoize-one';
import numbro from 'numbro';
import React from 'react';
import Button from 'react-bootstrap/Button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { FaEllipsisH, FaList } from 'react-icons/fa';

import { uiHelpers } from '../../helpers';
import { bsTiny } from '../../helpers/ui.helper';
import ParticipantNumberLevel, {
  participantNumberDataKey,
} from '../../modules/CCASS/ParticipantNumberLevel';
import TutorialQuestionMark from '../../modules/Help/TutorialQuestionMark';
import { Language } from '../../shared/types';
import { ReactTable } from '../../types';
import ConcernBarChart from '../visualize/ConcernBarChart';
import RelPriceChart from '../visualize/RelPriceChart';
import VolumeBarChart from '../visualize/VolumeBarChart';
import CCASSShortcutCell from './CCASSShortcutCell';
import {
  CANVAS_CELL_CLASS_NAME,
  DESKTOP_ONLY_CLASS_NAME,
} from './DLTable/DLTable';
import { PercentageChangeFilter, StringFilter, VolumeFilter } from './filters';

/**
 * Get rid of old interval "52w" -> "1y"
 */
const sanitizeInterval = (interval: string): string => {
  if (interval === '52w') {
    return '1y';
  }
  return interval;
};

const changeColumnId = 'Change';
export const changeColumn: (
  t: TFunction,
  priceChangeInterval: string,
  priceChangeFilter: React.FC,
  width?: number
) => ReactTable.Column = (
  t,
  priceChangeInterval,
  priceChangeFilter,
  width = 120
) => ({
  id: changeColumnId,
  Header: t('Change') + ` (${t(priceChangeInterval)})`,
  width,
  accessor: item =>
    item.closeBe1D === null
      ? item.close - item.open
      : item[`change${priceChangeInterval}`],
  Cell: function ChangeCell({ cell: { value } }) {
    return <div>{uiHelpers.formatDeltaMoney(value)}</div>;
  },
  Filter: priceChangeFilter,
  sortDescFirst: true,
});

// jscpd:ignore-start
export const changeColumnV2: (
  t: TFunction,
  priceChangeInterval: string,
  priceChangeFilter: React.FC,
  width?: number
) => ReactTable.Column = (
  t,
  priceChangeInterval,
  priceChangeFilter,
  width = 120
) => ({
  id: changeColumnId,
  Header: t('Change') + ` (${t(priceChangeInterval)})`,
  width,
  accessor: item => {
    const priceSummary = item['price_changes'][priceChangeInterval];
    if (!priceSummary) {
      return null;
    }
    return item['ref']['price'] - priceSummary[0];
  },
  Cell: function ChangeCell({ cell: { value }, row: { original: item } }) {
    return item['price_changes'][priceChangeInterval] ? (
      <OverlayTrigger
        placement="right"
        overlay={
          <Tooltip id="compare-to-date">
            {item['price_changes'][priceChangeInterval][1]}
          </Tooltip>
        }
      >
        <div>{uiHelpers.formatDeltaMoney(value)}</div>
      </OverlayTrigger>
    ) : (
      <span>-</span>
    );
  },
  Filter: priceChangeFilter,
  sortDescFirst: true,
});
// jscpd:ignore-end

const percentChange = (before, after) => {
  if (before === 0) return 1;
  return (after - before) / before;
};

export const changePercentageColumnID = '% Change';
function ChangePercentageCell({ cell: { value } }) {
  return (
    <div style={{ color: uiHelpers.colorForChange(value) }}>
      {numbro(value).format('+0.00%')}
    </div>
  );
}
export const changePercentageColumn: (
  t: TFunction,
  priceChangeInterval: string,
  width?: number
) => ReactTable.Column = (
  t: TFunction,
  priceChangeInterval: string,
  width = 100
) => ({
  id: changePercentageColumnID,
  Header: t('% Change') + ` (${t(priceChangeInterval)})`,
  width,
  accessor: item => {
    return item.closeBe1D === null
      ? percentChange(item.open, item.close)
      : percentChange(
          item.close - item[`change${priceChangeInterval}`],
          item.close
        );
  },
  Cell: ChangePercentageCell,
  Filter: PercentageChangeFilter,
  sortDescFirst: true,
});

export const changePercentageColumnV2: (
  t: TFunction,
  priceChangeInterval: string,
  width?: number
) => ReactTable.Column = (
  t: TFunction,
  priceChangeInterval: string,
  width = 100
) => ({
  id: changePercentageColumnID,
  Header: t('% Change') + ` (${t(priceChangeInterval)})`,
  width,
  accessor: item =>
    item['price_changes'][priceChangeInterval]
      ? percentChange(
          item['price_changes'][priceChangeInterval][0],
          item['ref']['price']
        )
      : null,
  Cell: ChangePercentageCell,
  Filter: PercentageChangeFilter,
  sortDescFirst: true,
});

export const totalNoColumnV2: (
  t: TFunction,
  participantNumChangeInterval: string,
  participantNumChangeFilter: React.FC,
  width?: number
) => ReactTable.Column = (
  t: TFunction,
  participantNumChangeInterval: string,
  participantNumChangeFilter: React.FC,
  width = 150
) => ({
  id: 'ranking-n-level',
  hkOnly: true,
  Header: (
    <span>
      {t('N Level') + ` (${t(participantNumChangeInterval)})`}{' '}
      <TutorialQuestionMark tutorialId={30} />
    </span>
  ),
  width,
  accessor: item => {
    const interval = sanitizeInterval(participantNumChangeInterval);
    const nopRanges = item['nop_ranges'][interval];
    if (!nopRanges) {
      return 0.5;
    }
    const [low, current, high] = nopRanges as [number, number, number];
    const span = high - low;
    if (span === 0) return 0.5;
    return (current - low) / span;
  },
  Cell: ({ row: { original: item } }) => {
    const interval = sanitizeInterval(participantNumChangeInterval);
    const nopRanges = item['nop_ranges'][interval];
    if (!nopRanges) {
      return <span>-</span>;
    }
    const [low, current, high] = nopRanges as [number, number, number];
    return (
      <ParticipantNumberLevel
        item={{
          total_no: current,
          [participantNumberDataKey(interval, 'min')]: low,
          [participantNumberDataKey(interval, 'max')]: high,
        }}
        participantNumChangeInterval={interval}
      />
    );
  },
  Filter: participantNumChangeFilter,
  filter: 'range',
  sortDescFirst: true,
  className: CANVAS_CELL_CLASS_NAME,
});

export const relVolumnColumnV2: (
  t: TFunction,
  avgVolInterval: string,
  width?: number
) => ReactTable.Column = (
  t: TFunction,
  avgVolInterval: string,
  width = 150
) => ({
  id: 'ranking-volume',
  Header: t('Vol. / Avg ()').replace('%s', t(avgVolInterval)),
  width,
  accessor: item => item.ref.volume / item.avg_vols[avgVolInterval],
  Cell: ({ row: { original: item } }) => (
    <VolumeBarChart
      item={{
        volume: item.ref.volume,
        [`avgVol${avgVolInterval}`]: item.avg_vols[avgVolInterval],
      }}
      interval={avgVolInterval}
    />
  ),
  Filter: VolumeFilter,
  sortDescFirst: true,
  className: CANVAS_CELL_CLASS_NAME,
});

const tipsWinPercentageCell = ({ cell: { value } }) => {
  return (
    <div style={{ color: uiHelpers.colorForChange(value) }}>
      {value === null ? '-' : value.toFixed(2) + '%'}
    </div>
  );
};

export const winPercentageColumn: (
  t: TFunction,
  priceChangeInterval: string,
  Filter: JSX.Element,
  width?: number
) => ReactTable.Column = (
  t: TFunction,
  priceChangeInterval: string,
  Filter: JSX.Element,
  width = 200
) => ({
  id: `tips-win-percentage-change`,
  Header: `${t('tips-return')}`,
  width,
  accessor: item => {
    return item[`winPercentage${priceChangeInterval}`];
  },
  Cell: tipsWinPercentageCell,
  Filter: Filter,
  filter: 'number',
  sortDescFirst: true,
});

export const RETURN_TILL_NOW_ID = 'tips-return-till-now';
export const returnTillNowColumn: (
  t: TFunction,
  width?: number
) => ReactTable.Column = (t: TFunction, width = 200) => ({
  id: RETURN_TILL_NOW_ID,
  Header: `${t(RETURN_TILL_NOW_ID)}`,
  width,
  accessor: item => {
    let priceNow = null;
    priceNow =
      item.market === 'hk'
        ? item.priceHK || item.indexPriceHK
        : item.priceUS || item.indexPriceUS;
    if (priceNow && item.price) {
      const absoluteReturn = ((priceNow - item.price) / item.price) * 100;
      return item.direction === 'bullish' ? absoluteReturn : -absoluteReturn;
    }
    return null;
  },
  Filter: PercentageChangeFilter,
  Cell: tipsWinPercentageCell,
  sortDescFirst: true,
});

export const indexColumn: () => ReactTable.Column = memoizeOne(() => ({
  Header: '#',
  id: 'rowIndex',
  width: 24,
  disableFilters: true,
  Cell: function IndexCell({ row }) {
    let fontSize = 12;
    if (row.rowIndex >= 10) fontSize = 10;
    if (row.rowIndex >= 100) fontSize = 8;
    if (row.rowIndex >= 1000) fontSize = 6;
    return (
      <div style={{ width: '100%', fontSize: `${fontSize}px` }}>
        {row.rowIndex}
      </div>
    );
  },
  className: 'row-index-cell',
  desktopOnly: true,
}));

export const ccassShortcutColumn: () => ReactTable.Column = memoizeOne(() => ({
  id: 'ccass_shortcut',
  hkOnly: true,
  Header: <FaEllipsisH />,
  width: 30,
  Cell: CCASSShortcutCell,
  disableFilters: true,
  contentAlign: 'center',
}));

const relPriceColumnID = 'relPrice';
export const relPriceColumn: (
  t: TFunction,
  priceRangeInterval: string,
  Filter: React.FC<ReactTable.FilterProps>,
  width?: number
) => ReactTable.Column = (t, priceRangeInterval, Filter, width = 150) => ({
  id: relPriceColumnID,
  Header: t('Price Range'),
  width,
  accessor: item => {
    const span =
      item[`highest${priceRangeInterval}`] -
      item[`lowest${priceRangeInterval}`];
    if (span === 0) return 0.5;
    return (item.close - item[`lowest${priceRangeInterval}`]) / span;
  },
  Cell: function RelPriceCell({ row: { original: item } }) {
    return (
      <RelPriceChart item={item} priceRangeInterval={priceRangeInterval} />
    );
  },
  className: classNames(CANVAS_CELL_CLASS_NAME, DESKTOP_ONLY_CLASS_NAME),
  Filter,
  filter: 'range',
  sortDescFirst: true,
});

const concentrationColumnID = 'concentration';
export const concentrationColumn: (
  t: TFunction,
  topN: number,
  Filter: React.FC<ReactTable.FilterProps>,
  width?: number
) => ReactTable.Column = (t, topN, Filter, width = 150) => ({
  id: concentrationColumnID,
  hkOnly: true,
  Header: t('Concentration'),
  width,
  accessor: item => {
    if (item.concentration_ranges) {
      const threeMonthConcentration = item.concentration_ranges['3m'];
      if (threeMonthConcentration) {
        return threeMonthConcentration[String(topN)][1];
      }
    }
  },
  Cell: function ConcenCell({ cell: { value } }) {
    return value ? <ConcernBarChart concern={value} /> : <span>-</span>;
  },
  className: classNames(CANVAS_CELL_CLASS_NAME, DESKTOP_ONLY_CLASS_NAME),
  Filter,
  filter: 'range',
  sortDescFirst: true,
});

export const relPriceColumnV2: (
  t: TFunction,
  priceRangeInterval: string,
  Filter: React.FC<ReactTable.FilterProps>,
  width?: number
) => ReactTable.Column = (t, priceRangeInterval, Filter, width = 150) => ({
  id: relPriceColumnID,
  Header: t('Price Range'),
  width,
  accessor: item => {
    priceRangeInterval = sanitizeInterval(priceRangeInterval);
    if (!item['price_ranges'][priceRangeInterval]) return 0.5;
    const [low, high] = item['price_ranges'][priceRangeInterval];
    const span = high - low;
    if (span === 0) return 0.5;
    return (item.ref.price - low) / span;
  },
  Cell: function RelPriceCell({ row: { original: item } }) {
    if (!item['price_ranges'][priceRangeInterval]) return <span>-</span>;
    priceRangeInterval = sanitizeInterval(priceRangeInterval);
    const [low, high] = item['price_ranges'][priceRangeInterval];
    return (
      <RelPriceChart
        item={{
          low: item.ref.low,
          high: item.ref.high,
          close: item.ref.price,
          [`lowest${priceRangeInterval}`]: low,
          [`highest${priceRangeInterval}`]: high,
        }}
        priceRangeInterval={priceRangeInterval}
      />
    );
  },
  className: classNames(CANVAS_CELL_CLASS_NAME, DESKTOP_ONLY_CLASS_NAME),
  Filter,
  filter: 'range',
  sortDescFirst: true,
});

const participantIdColumnID = 'participantId';
export const participantIdColumn: (
  t: TFunction,
  i18n: i18n,
  handleClickParticipantHistory: (
    stockCode: string,
    participantId: string,
    participantName: string
  ) => void,
  width?: number
) => ReactTable.Column = (
  t: TFunction,
  i18n: i18n,
  handleClickParticipantHistory: (
    stockCode: string,
    participantId: string,
    participantName: string
  ) => void,
  width = 100
) => ({
  id: participantIdColumnID,
  Header: t('Participant ID'),
  width,
  accessor: 'participantId',
  Cell: function ParticipantIdCell({ cell: { value }, row: { original } }) {
    return (
      <div>
        {value}
        <Button
          variant="link"
          size={bsTiny}
          onClick={() => {
            handleClickParticipantHistory(
              original.stockCode || original.StockCode,
              original.participantId,
              original.participantName[i18n.language as Language]
            );
          }}
        >
          <FaList />
        </Button>
      </div>
    );
  },
  Filter: StringFilter,
  filter: 'text',
});
